import { FiTrendingUp } from 'react-icons/fi';

export const modalVerbsData = {
  courseId: 'modal-verbs',
  courseType: 'grammar',
  title: 'Modal Verbs',
  icon: FiTrendingUp,
  overview: {
    impact: 'Essential for expressing ability, possibility, obligation',
    focus: 'Using can, could, may, might, should, must, etc.',
    uses: 'Writing Task 2, Speaking',
    duration: '25 minutes',
  },
  sections: [
    {
      title: 'Modal Verbs Overview',
      content: `<p>Modal verbs add meaning about possibility, ability, permission, or obligation. They never change form and are always followed by the base verb.</p>`,
      table: {
        headers: ['Modal', 'Primary Use', 'Example'],
        rows: [
          ['can/could', 'Ability/Possibility', 'She can speak French.'],
          ['may/might', 'Possibility/Permission', 'It might rain later.'],
          ['should/ought to', 'Advice/Recommendation', 'You should study harder.'],
          ['must/have to', 'Obligation/Necessity', 'You must wear a seatbelt.'],
          ['would', 'Hypothetical/Polite', 'I would help if I could.'],
        ],
      },
    },
    {
      title: 'Modals for Speculation',
      content: `<p>Use modals to express different levels of certainty (useful in IELTS Writing Task 2):</p>`,
      points: [
        'must be: Very certain (95%+) - "He must be tired after that journey."',
        'should/ought to: Probable (75%) - "The results should be ready soon."',
        'may/might/could: Possible (50%) - "Technology may create new jobs."',
        'can\'t be: Very unlikely - "That can\'t be true."',
      ],
    },
    {
      title: 'Modals in IELTS Writing',
      content: `<p>Modals help you express opinions with appropriate hedging:</p>`,
      examples: [
        { label: 'Strong', text: 'Governments must invest in renewable energy.' },
        { label: 'Moderate', text: 'Governments should consider investing in renewable energy.' },
        { label: 'Tentative', text: 'Governments might benefit from investing in renewable energy.' },
      ],
      warning: 'Using modals shows sophistication. Avoid being too absolute in your claims!',
    },
  ],
  exercises: [
    {
      title: 'Choose the Right Modal',
      type: 'select',
      instructions: 'Select the most appropriate modal verb.',
      questions: [
        {
          text: 'You ___ wear a seatbelt. It\'s the law.',
          options: ['must', 'might', 'could'],
          answer: 'must',
          explanation: '"Must" expresses legal obligation—it\'s required by law.',
        },
        {
          text: 'It ___ rain tomorrow, but I\'m not sure.',
          options: ['might', 'must', 'can'],
          answer: 'might',
          explanation: '"Might" expresses uncertainty/possibility (about 50% sure).',
        },
        {
          text: 'You ___ see a doctor about that cough.',
          options: ['should', 'must', 'might'],
          answer: 'should',
          explanation: '"Should" gives advice/recommendation.',
        },
        {
          text: 'He ___ speak three languages fluently.',
          options: ['can', 'should', 'might'],
          answer: 'can',
          explanation: '"Can" expresses ability—what someone is capable of doing.',
        },
        {
          text: 'The package ___ arrive today. I ordered express delivery.',
          options: ['should', 'might', 'must'],
          answer: 'should',
          explanation: '"Should" expresses expectation/probability (likely to happen).',
        },
        {
          text: '___ I help you with your luggage?',
          options: ['May', 'Must', 'Should'],
          answer: 'May',
          explanation: '"May I" is a polite way to offer help.',
        },
      ],
    },
    {
      title: 'Certainty Levels',
      type: 'select',
      instructions: 'Order these sentences from most certain to least certain.',
      questions: [
        {
          text: 'Which expresses the HIGHEST certainty?',
          options: ['She must be the manager.', 'She might be the manager.', 'She could be the manager.'],
          answer: 'She must be the manager.',
          explanation: '"Must" shows 95%+ certainty based on evidence.',
        },
        {
          text: 'Which expresses the LOWEST certainty?',
          options: ['It could be true.', 'It must be true.', 'It should be true.'],
          answer: 'It could be true.',
          explanation: '"Could" shows only about 30-50% certainty—just a possibility.',
        },
        {
          text: 'Which shows strong probability (around 80%)?',
          options: ['The results should be ready.', 'The results might be ready.', 'The results could be ready.'],
          answer: 'The results should be ready.',
          explanation: '"Should" shows strong expectation—likely but not 100% certain.',
        },
      ],
    },
    {
      title: 'Obligation vs Recommendation',
      type: 'select',
      instructions: 'Is this an obligation (must do) or a recommendation (should do)?',
      questions: [
        {
          text: 'Students must submit assignments by Friday.',
          options: ['Obligation', 'Recommendation'],
          answer: 'Obligation',
          explanation: '"Must" indicates a requirement—there\'s no choice.',
        },
        {
          text: 'Students should review their notes regularly.',
          options: ['Recommendation', 'Obligation'],
          answer: 'Recommendation',
          explanation: '"Should" is advice—good to do but not required.',
        },
        {
          text: 'Passengers have to show ID at security.',
          options: ['Obligation', 'Recommendation'],
          answer: 'Obligation',
          explanation: '"Have to" (like must) indicates a requirement.',
        },
      ],
    },
    {
      title: 'Hedging in IELTS Writing',
      type: 'select',
      instructions: 'Which sentence is appropriately hedged for IELTS Task 2?',
      questions: [
        {
          text: 'Which is best for an IELTS opinion essay?',
          options: ['Technology could potentially create new job opportunities.', 'Technology will definitely create new jobs.', 'Technology creates new jobs.'],
          answer: 'Technology could potentially create new job opportunities.',
          explanation: 'Using "could potentially" shows appropriate academic hedging.',
        },
        {
          text: 'Which avoids being too absolute?',
          options: ['This may lead to problems.', 'This leads to problems.', 'This definitely leads to problems.'],
          answer: 'This may lead to problems.',
          explanation: '"May" hedges the claim—academic writing avoids absolute statements.',
        },
      ],
    },
    {
      title: 'Write with Modals',
      type: 'writing',
      instructions: 'Rewrite these sentences using appropriate modals.',
      questions: [
        {
          text: 'Rewrite with uncertainty: "Online learning is more effective than traditional learning."',
          example: 'Online learning may/might/could be more effective than traditional learning in some contexts.',
          tip: 'Use may/might/could to show this is a possibility, not a fact.',
        },
        {
          text: 'Rewrite as advice: "People exercise more to stay healthy."',
          example: 'People should exercise more to maintain their health.',
          tip: 'Use "should" for recommendations.',
        },
        {
          text: 'Rewrite as strong obligation: "Companies reduce their carbon emissions."',
          example: 'Companies must reduce their carbon emissions to address climate change.',
          tip: 'Use "must" for strong necessity/obligation.',
        },
      ],
    },
  ],
  tips: [
    'Use modals to hedge opinions in Task 2',
    'Match modal strength to your certainty level',
    'Practice past modals: could have, should have, might have',
  ],
  dailyPractice: [
    'Write sentences with different certainty levels',
    'Practice giving advice with modals',
    'Identify modals in IELTS reading passages',
  ],
  backPath: '/exams/english/#grammar',
  backLabel: 'Back to Grammar Concepts',
  aiContext: 'Modal Verbs: Auxiliary verbs (can, could, may, might, should, must) that express ability, possibility, permission, obligation, and speculation.',
};

// Difficulty-variant factory: returns content adapted to the given difficulty level.
// difficulty 1-2: simplified content with basic examples and easier exercises
// difficulty 3: base content (default)
// difficulty 4-5: advanced content with complex examples and harder exercises
export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...modalVerbsData,
      title: modalVerbsData.title + ' (Foundations)',
      overview: {
        ...modalVerbsData.overview,
        duration: '15-20 minutes',
      },
      // Simplified: show only first 2 sections and first exercise set
      sections: modalVerbsData.sections.slice(0, Math.max(2, Math.ceil(modalVerbsData.sections.length / 2))),
      exercises: modalVerbsData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...modalVerbsData,
      title: modalVerbsData.title + ' (Advanced)',
      overview: {
        ...modalVerbsData.overview,
        duration: '30-35 minutes',
      },
      // Advanced: all sections plus advanced tips context
      sections: modalVerbsData.sections,
      exercises: modalVerbsData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(modalVerbsData.tips || []),
        'For Band 8+: Focus on natural-sounding usage rather than just accuracy.',
        'Advanced tip: Integrate this concept fluidly in timed IELTS writing tasks.',
      ],
    };
  }
  return modalVerbsData;
}
